import * as Yup from 'yup';
import { useContext } from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { useEffect, useRef, useState } from 'react';
import { EditDynamicFormMap } from './schema';
import { TenantLink, DTVendable, FBD_Vendable } from '@rabbit/data/types';
import {
  RequiredType,
  FieldConfig,
  AdditionalFields,
  DisplayType,
} from '@rabbit/helper';
import { createDynamicYupSchema } from '@rabbit/app-context';
import {
  Button,
  Modal,
  Input,
  ButtonIcon,
} from '@rabbit/elements/shared-components';
import SageFormBuilder from 'libs/elements/shared-components/src/lib/molecules/SageFormBuilder/SageFormBuilder';
import { nestApiGetMakesModels } from '@rabbit/bizproc/core';
import { PencilIcon } from '@heroicons/react/24/solid';
import { UserContext } from '@rabbit/sage/context/UserContext';
import { useGetMySagePersonas } from '@rabbit/data/portal';

export enum FormSchemaEnum {
  REGISTRATION = 'REGISTRATION',
  WARRANTY = 'WARRANTY',
}

interface ModalEditDynamicFormProps {
  title: string;
  tenantLink: TenantLink;
  formType: FormSchemaEnum;
  handleSubmit: any;
  handleClose: any;
  initialData?: Record<string, any>;
  isLoading?: boolean;
}

interface CurrencyValue {
  amount: string | number;
  currency: string;
}

const ModalEditDynamicForm = ({
  tenantLink,
  formType,
  handleSubmit,
  handleClose,
  title,
  initialData = {},
  isLoading = false,
}: ModalEditDynamicFormProps) => {
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const { tenantInfo, config } = useContext(AppContext);
  const { activeTenantInfo } = useContext(UserContext) || {};
  const { warrantorPersona } = useGetMySagePersonas();
  const isWarrantor = !!warrantorPersona?.personaId;
  const isPartnerToTenant =
    activeTenantInfo?.isPartnerTo?.length &&
    activeTenantInfo?.isPartnerTo?.length > 0 &&
    activeTenantInfo?.isPartnerTo?.includes(t('tenantLink'));
  const filterFieldsByDisplaySettings = (
    fields: AdditionalFields,
    isPartnerToTenant: boolean
  ): AdditionalFields => {
    return Object.entries(fields)
      .filter(([_, field]) => {
        // If no display property or empty display array, allow the field
        if (!field.display || field.display.length === 0) {
          return true;
        }

        return field.display.includes(
          isPartnerToTenant
            ? DisplayType.DISPLAY_FOR_PARTNERS // Display for partners if the user is a partner to the tenant
            : DisplayType.DISPLAY_FOR_TENANT // Otherwise, display for the tenant
        );
      })
      .reduce((acc, [key, field]) => {
        acc[key] = field;
        return acc;
      }, {} as AdditionalFields);
  };
  const [fields, setFields] = useState<any>(
    t('tenantLink') === 'SUPAGARD' && formType == FormSchemaEnum.REGISTRATION
      ? filterFieldsByDisplaySettings(
          EditDynamicFormMap[tenantLink][formType],
          !!isPartnerToTenant
        )
      : EditDynamicFormMap[tenantLink][formType]
  );
  const [formSchema, setFormSchema] = useState<any>();
  const [productData, setProductData] = useState(initialData.vendable || null);
  const [vendableItem, setVendableItem] = useState({} as DTVendable);
  const [productLoaded, setProductLoaded] = useState(false);
  const defaultSelected = initialData.vendable || null;
  const modalSettings = {
    kind: 'generic' as const,
    settings: {
      title: title,
      text: title,
      handleClose: () => handleClose(),
      childrenClassName: 'overflow-auto',
    },
  };

  useEffect(() => {
    (async () => {
      if (productData && productData?.value && !productLoaded) {
        setProductLoaded(true);
        const vendable = await FBD_Vendable.get(productData.value);
        if (vendable) {
          setVendableItem(vendable);
        }
      }
    })();
  }, [productData, productLoaded]);

  // Create initial values with proper currency structure
  const getInitialValues = () => {
    const initialValues: any = {
      holding_vendable_id: initialData.holding_vendable_id || '',
      ...(initialData.serial_number ? { serial_number: initialData.serial_number } : {}),
    };

    // Include purchase_time initial value when edit of purchase date is enabled
    if (config?.HOLDINGS?.REGISTRATION?.CAN_EDIT_PURCHASE_DATE && isWarrantor) {
      // Ensure Formik receives a Date object (or null) so Yup.date() validation works correctly
      initialValues['purchase_time'] = initialData?.purchase_time
        ? new Date(initialData.purchase_time)
        : null;
    }
    Object.entries(fields).forEach(([key, field]) => {
      const typedField = field as FieldConfig;
      const apiValue = initialData[key];

      if (typedField.type === 'currency') {
        if (apiValue && typeof apiValue === 'object' && 'amount' in apiValue) {
          initialValues[key] = {
            amount: apiValue.amount || '',
            currency: apiValue.currency || tenantInfo?.currency || '',
          };
        } else if (apiValue && typeof apiValue === 'number') {
          initialValues[key] = {
            amount: apiValue,
            currency: tenantInfo?.currency || '',
          };
        } else {
          // Fallback to field's initial value or empty
          const initialValue = typedField.initialValue as CurrencyValue;
          initialValues[key] = {
            amount: initialValue?.amount || '',
            currency: initialValue?.currency || tenantInfo?.currency || '',
          };
        }
      } else {
        initialValues[key] =
          apiValue !== undefined ? apiValue : typedField.initialValue;
      }
    });
    return initialValues;
  };

  const getMakesModels = async () => {
    const data: any = await nestApiGetMakesModels({ tenantLink: tenantLink });
    let tempFields = { ...fields };
    if (data.makes) {
      tempFields['make'].suggestion =
        data.makes?.map((s: any) => s.label) || [];
    }
    if (data.models) {
      tempFields['model'].suggestion =
        data.models?.map((s: any) => s.label) || [];
    }
    setFields(tempFields);
  };

  const generateSchema = async () => {
    const { MAKE_TAB, MODEL_TAB } = config;
    if (MAKE_TAB.ENABLED && MODEL_TAB.ENABLED) {
      await getMakesModels();
    }
    try {
      const generatedSchema = createDynamicYupSchema(
        fields,
        RequiredType.REQUIRED_FOR_ALL,
        undefined,
        {
          currency: (field) => {
            let schema = Yup.object().shape({
              amount: Yup.number()
                .typeError(t('Must be a number'))
                .required(t('Required')),
              currency: Yup.string().required(t('Required')),
            });
            return schema;
          },
        }
      );
      // If enabled, add validation for purchase_time
      let finalSchema = generatedSchema;
      if (
        config?.HOLDINGS?.REGISTRATION?.CAN_EDIT_PURCHASE_DATE &&
        isWarrantor
      ) {
        const purchaseTimeSchema = Yup.object().shape({
          purchase_time: Yup.date()
            .max(new Date(), 'Please enter a valid date')
            .required('Please select a purchase time')
            .typeError('Please enter a valid date'),
        });
        finalSchema = generatedSchema
          ? generatedSchema.concat(purchaseTimeSchema)
          : purchaseTimeSchema;
      }

      if (config?.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER) {
        const serialNumberSchema = Yup.object().shape({
          serial_number: Yup.string().trim().optional(),
        });
        finalSchema = finalSchema.concat(serialNumberSchema);
      }

      if (finalSchema) {
        setFormSchema(finalSchema);
      } else {
        setFormSchema(null);
      }
    } catch (error) {
      console.error('Error generating schema:', error);
      setFormSchema(null); // Or set a default schema
    }
  };

  useEffect(() => {
    generateSchema();
  }, [tenantLink, formType, t]);

  return (
    <Modal
      className="max-w-[724px]"
      kind={'generic'}
      settings={modalSettings.settings}
    >
      {formSchema ? (
        <div className="space-y-2 p-4">
          <Formik
            initialValues={getInitialValues()}
            onSubmit={handleSubmit}
            validationSchema={formSchema}
            validateOnChange={true}
            validateOnBlur={true}
            enableReinitialize={true}
            innerRef={formikRef}
          >
            {(props) => (
              <Form>
                <div className="mb-4">
                  <div
                    className={`mt-4 grid ${
                      config.HOLDINGS.REGISTRATION.CAN_EDIT_PURCHASE_DATE &&
                      isWarrantor
                        ? 'grid-cols-2'
                        : 'grid-cols-1'
                    } gap-4`}
                  >
                    <div className={`relative h-auto w-full`}>
                      <Input
                        type="autocomplete-vendable"
                        name="holding_vendable_id"
                        label={`${t('general.product')}*`}
                        settings={{
                          isMulti: false,
                          id: 'holding_vendable_id',
                          placeholder: t('general.productName'),
                          options: [],
                          errors: props.errors,
                          hint: t('*required'),
                          tenantLink: tenantLink,
                          customButton: config?.HOLDINGS?.NEW_REGISTRATION_FLOW
                            ?.ADD_NEW_PRODUCT ? (
                            <Button
                              kind="primary"
                              className="w-full"
                              onClick={() => handleClose()}
                            >
                              {t('general.addNewProduct')}
                            </Button>
                          ) : null,
                          disabled: !!defaultSelected,
                          value: defaultSelected,
                        }}
                        onChange={(items) => {
                          setProductData(items);
                          setProductLoaded(false);
                          props.setFieldValue(
                            'holding_vendable_id',
                            items?.value || ''
                          );
                        }}
                      />
                      {defaultSelected?.id &&
                        config?.HOLDINGS?.NEW_REGISTRATION_FLOW
                          ?.UNIQUE_PRODUCT && (
                          <ButtonIcon
                            label={''}
                            className={`absolute right-4 top-9`}
                            Icon={PencilIcon}
                            iconLeft
                            onClick={() => handleClose()}
                          />
                        )}
                    </div>
                    <div className={`h-auto w-full`}>
                      {config.HOLDINGS.REGISTRATION.CAN_EDIT_PURCHASE_DATE &&
                        isWarrantor && (
                          <Input
                            type="datepicker"
                            name="purchase_time"
                            label={`${t('general.purchaseDate')} ${
                              config?.HOLDINGS.MANDATORY_FIELDS.PURCHASE_TIME
                                ? '*'
                                : ''
                            }`}
                            settings={{
                              id: 'purchase_time',
                              placeholder: t('general.purchaseDate'),
                              maxDate: new Date(),
                              hint: t('*required'),
                            }}
                          />
                        )}
                    </div>
                    <div className={`h-auto w-full`}>
                      {config.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER && (
                        <Input
                          type="text"
                          name="serial_number"
                          label={t('general.serialNumber')}
                          settings={{
                            placeholder: t('general.serialNumber')
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <SageFormBuilder fields={fields} maxGridCount={2} />
                <div className="mt-4 grid w-full grid-cols-2 gap-2">
                  <Button kind="primary" type="submit" disabled={isLoading}>
                    {t('general.update')}
                  </Button>
                  <Button
                    kind="outline_red"
                    type="submit"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    {t('general.cancel')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      ) : (
        <p className="p-4">
          {t('general.noSchemaDefinedForThisTenantAndFormType', {
            tenantLink: tenantLink || 'unknown',
            formType: formType || 'unknown',
          })}
        </p>
      )}
    </Modal>
  );
};

export default ModalEditDynamicForm;
